import * as React from 'react';
import { Expander } from 'sos-common/js/components/Expander/Expander';
import { Fileupload } from 'sos-common/js/components/Fileupload/Fileupload';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import { FileExtendedDetails, FileState } from 'sos-common/js/commonTypes/fileupload.types';
import { STATSBIDRAG_COMPLETION_MIMETYPES } from './StatsbidragCompletion.mimetypes';
import {
  StatsbidragCompletionProps,
  StatsbidragCompletionState,
  GetAttachmentsServerResponseObject,
  API_ENDPOINT_FRIVILLIG,
  API_ENDPOINT_REQUIRED,
  PostFilesServerResponseObject,
} from './StatsbidragCompletion.types';
import { Dialog } from 'sos-common/js/components/Dialog/Dialog';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { sendCompletion } from 'sos-eservice-common/js/services/completion.service';
import { postFile, getFiles, deleteFile } from 'sos-eservice-common/js/services/files.service';
import { stringWithLineBreak } from 'sos-common/js/utils/helpers';
import { findLastIndex } from 'lodash';

/**
 * Contains the logic for uploading and deleting a complementary file, and getting unsent files of a completion.
 * One file can be uploaded/deleted at a time; whereas all unsent files are received after component is mounted.
 */
export class StatsbidragCompletion extends React.Component<StatsbidragCompletionProps, StatsbidragCompletionState> {
  constructor(props: StatsbidragCompletionProps) {
    super(props);

    this.state = {
      unlockSendButton: false,
      fileUploadState: [] as FileExtendedDetails[],
      serverResponseObject: null,
      complementaryId: props.complementaryId ? props.complementaryId : null,
      clientError: null,
      showDialog: false,
      sendSuccess: null,
      sendCompletionErrorMessage: '',
    };
    this.getCurrentAttachments();
  }

  getApiEndpoint = () => {
    if (this.props.isRequiredCompletion) return API_ENDPOINT_REQUIRED;
    return API_ENDPOINT_FRIVILLIG;
  };

  setComplementaryId = (complementaryId: string) => {
    this.setState({
      complementaryId: complementaryId,
    });
  };

  setErrorMessage = (errorMessage: string) => {
    this.setState({
      clientError: {
        error: null,
        errorMessage: errorMessage || 'Ett ohanterat fel inträffade.',
      },
    });
  };

  onAddFile = (file: FormFieldFile) => {
    const { complementaryId } = this.state;
    const { caseId } = this.props;
    const apiEndpoint = this.getApiEndpoint() + '/attach';
    const formData = new FormData();

    formData.append('caseId', caseId);
    if (complementaryId) formData.append('completionId', complementaryId);

    this.setState({
      fileUploadState: file.value,
    });

    postFile<PostFilesServerResponseObject>(file, apiEndpoint, formData).then((response) => {
      if (response instanceof ResponseError) {
        this.setErrorMessage(response.body.errorMessage);
      } else {
        this.setComplementaryId(response.serverResponse.ComplementaryId);
        this.setState({
          unlockSendButton: response.serverResponse.ComplementaryId ? true : false,
          fileUploadState: response.fileUploadValue,
          serverResponseObject: response.serverResponse,
          clientError: null,
        });
      }
    });
  };

  onSendClick = () => {
    const { complementaryId } = this.state;
    const { caseId } = this.props;
    const apiEndpoint = this.getApiEndpoint() + '/submit';
    const formData = new FormData();

    formData.append('caseId', caseId);
    formData.append('completionId', complementaryId);

    sendCompletion(apiEndpoint, formData).then((response) => {
      if (response instanceof ResponseError) {
        this.setState({
          showDialog: true,
          sendSuccess: false,
          sendCompletionErrorMessage: response.body.errorMessage || 'Ett ohanterat fel inträffade.',
        });
      } else {
        this.setState({
          showDialog: true,
          sendSuccess: true,
        });
      }
    });
  };

  closeConfirm = () => {
    this.setState({
      showDialog: false,
    });
    window.location.reload();
  };

  getCurrentAttachments = () => {
    const { caseId, type } = this.props;
    const { complementaryId } = this.state;
    const apiEndpoint = this.getApiEndpoint() + `/get?caseId=${caseId}&type=${type}&complementaryId=${complementaryId}`;

    getFiles<GetAttachmentsServerResponseObject>(apiEndpoint).then((response) => {
      if (response instanceof ResponseError) {
        this.setErrorMessage(response.body.errorMessage);
      } else {
        this.setComplementaryId(response.CompletionId);
        if (response.Files && response.Files.length > 0) {
          let fileExtendedDetails: FileExtendedDetails[] = [];
          response.Files.forEach((value) => {
            fileExtendedDetails.push({
              FileId: value.Id,
              OriginalFilename: value.Filnamn,
              status: FileState.SETTLED,
            });
          });
          this.setState({
            fileUploadState: fileExtendedDetails,
            unlockSendButton: response.CompletionId ? true : false,
          });
        }
      }
    });
  };

  onDeleteFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    const { caseId } = this.props;
    const { complementaryId } = this.state;
    const apiEndpoint = this.getApiEndpoint() + '/delete';
    const formData = new FormData();
    formData.append('caseId', caseId);
    formData.append('fileId', file.FileId);
    formData.append('completionId', complementaryId);

    deleteFile(apiEndpoint, formData).then((response) => {
      if (response instanceof ResponseError) {
        this.setErrorMessage(response.body.errorMessage);
      } else {
        const fileId = response;
        let newFileUploadState = this.state.fileUploadState.slice();
        let index = newFileUploadState.findIndex((val) => {
          return val.FileId === fileId;
        });
        newFileUploadState.splice(index, 1);
        this.setState({
          fileUploadState: newFileUploadState,
          clientError: null,
          unlockSendButton: newFileUploadState.length > 0,
        });
      }
    });
  };

  reloadPage = () => {
    window.location.reload();
  };

  renderSuccessDialog = () => {
    return (
      <Dialog isOpen={this.state.showDialog} onRequestClose={this.closeConfirm}>
        <button onClick={this.closeConfirm} className="close">
          <Icon isAriaHidden={false} ariaLabel="Stäng" iconType="ic_close_white_24px" />
        </button>
        <div className="header">
          <h2>{'Uppladdningen lyckades'}</h2>
        </div>
        <p>{'Din komplettering är nu skickad.'}</p>
        <div className="footer">
          <input type="submit" className="button" onClick={this.reloadPage} value={'Stäng'} />
        </div>
      </Dialog>
    );
  };

  renderFailDialog = () => {
    return (
      <Dialog isOpen={this.state.showDialog} onRequestClose={this.closeConfirm}>
        <button onClick={this.closeConfirm} className="close">
          <Icon isAriaHidden={false} ariaLabel="Stäng" iconType="ic_close_white_24px" />
        </button>
        <div className="header">
          <h2>Ett oväntat fel har inträffat</h2>
        </div>
        <p>Försök skicka in kompletteringen igen om en stund.</p>
        <div className="error-code">{this.state.sendCompletionErrorMessage}</div>
      </Dialog>
    );
  };

  public render() {
    const {
      expandHeading,
      expandIcon,
      expandCssClass,
      fileHeading,
      innerHeading,
      innerParagraph,
      documentList,
      isRequiredCompletion,
    } = this.props;
    return (
      <div className={`white-panel ${isRequiredCompletion ? 'attentionbox--error' : ''}`}>
        {isRequiredCompletion ? (
          <div className="statsbidrag-completion">
            <Icon
              className="attentionbox__icon"
              iconType="fontawesome/exclamation-circle-solid"
              ariaLabel=""
              isAriaHidden={true}
            />
            <h4>{expandHeading}</h4>
          </div>
        ) : (
          <h4>{expandHeading}</h4>
        )}

        <p
          className="statsbidrag-completion__innerpara"
          dangerouslySetInnerHTML={{ __html: innerParagraph && stringWithLineBreak(innerParagraph) }}></p>
        {documentList && (
          <div>
            <br />
            {documentList.map((item) => {
              return (
                <div key={item.fileName} className="filelist-item filelist-item--nodate">
                  <div className="filelist-item__name">
                    <a className={`file-extension-icon ${item.fileExtension}`} href={item.url}>
                      {item.fileName}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* <Expander isExpanded={false} heading={expandHeading} icon={expandIcon} cssClass={expandCssClass}> */}
        <Fileupload
          disabled={false}
          max={25}
          heading=""
          upload="Ladda upp fil"
          value={this.state.fileUploadState}
          nofilesText="Du har inte laddat upp någon fil ännu"
          mimetype={STATSBIDRAG_COMPLETION_MIMETYPES}
          onAdd={this.onAddFile}
          onRemove={this.onDeleteFile}
          maxFileSize={157286400} // 150 mb
          clientError={this.state.clientError}
          useDesignSystemLayout={true}
        />
        <button disabled={this.state.unlockSendButton ? false : true} onClick={this.onSendClick} className="button">
          Skicka komplettering
        </button>

        {/* </Expander> */}

        {this.state.sendSuccess === true && this.renderSuccessDialog()}

        {this.state.sendSuccess === false && this.renderFailDialog()}
      </div>
    );
  }
}
